import React, { useState, useMemo, useRef } from "react";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { searchStations } from "@/lib/stations";
import { X, Plus } from "lucide-react";

/**
 * Multi-select stops picker.
 * Maintains an ordered list of stop chips, with autocomplete to add stations.
 * Users can also type custom strings and press Enter.
 */
export default function StationsPicker({ value = [], onChange, testIdPrefix = "stops" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  const suggestions = useMemo(() => {
    const filtered = searchStations(query, 50).filter(
      (s) => !value.includes(s)
    );
    return filtered;
  }, [query, value]);

  const add = (station) => {
    const s = (station || "").trim();
    if (!s) return;
    if (value.includes(s)) return;
    onChange([...value, s]);
    setQuery("");
    // Re-focus the input
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const removeAt = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const moveStop = (from, to) => {
    if (to < 0 || to >= value.length) return;
    const next = [...value];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-picker`}>
      {/* Chips */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 p-2 border border-[#2A2A2A] bg-[#0E0E0E] min-h-[44px]">
          {value.map((s, idx) => (
            <span
              key={`${s}-${idx}`}
              data-testid={`${testIdPrefix}-chip-${idx}`}
              className="group inline-flex items-center gap-1 px-2 py-1 bg-[#1a1a1a] border border-[#2A2A2A] hover:border-white/40 font-mono text-[11px] text-white"
            >
              <span className="text-white/40">{idx + 1}.</span>
              <button
                type="button"
                onClick={() => moveStop(idx, idx - 1)}
                disabled={idx === 0}
                className="text-white/40 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed text-[10px] leading-none"
                aria-label="Move up"
              >
                ↑
              </button>
              <button
                type="button"
                onClick={() => moveStop(idx, idx + 1)}
                disabled={idx === value.length - 1}
                className="text-white/40 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed text-[10px] leading-none"
                aria-label="Move down"
              >
                ↓
              </button>
              <span>{s}</span>
              <button
                type="button"
                onClick={() => removeAt(idx)}
                data-testid={`${testIdPrefix}-chip-remove-${idx}`}
                className="ml-1 text-white/50 hover:text-red-500"
                aria-label={`Remove ${s}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Add input with autocomplete */}
      <Popover open={open} onOpenChange={setOpen}>
        <div className="flex items-stretch gap-2">
          <PopoverTrigger asChild>
            <div className="relative flex-1">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  if (!open) setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    add(suggestions[0] ?? query);
                  }
                }}
                placeholder="Add a stop (e.g. Strathfield)…"
                data-testid={`${testIdPrefix}-input`}
                className="flex h-10 w-full px-3 py-2 text-sm bg-[#111] border border-[#2A2A2A] text-white placeholder:text-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A0A0A] font-mono"
              />
            </div>
          </PopoverTrigger>
          <button
            type="button"
            onClick={() => add(query)}
            disabled={!query.trim()}
            data-testid={`${testIdPrefix}-add-button`}
            className="px-3 border border-[#2A2A2A] bg-[#111] text-white hover:bg-white hover:text-black disabled:opacity-40 disabled:cursor-not-allowed font-mono uppercase tracking-widest text-[11px] flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>
        <PopoverContent
          align="start"
          sideOffset={4}
          className="p-0 rounded-none bg-[#111] border-[#2A2A2A] w-[var(--radix-popover-trigger-width)] min-w-[280px]"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command className="bg-[#111] text-white rounded-none" shouldFilter={false}>
            <CommandList className="max-h-64">
              <CommandEmpty className="py-4 text-xs font-mono text-white/40 uppercase tracking-wider">
                {query.trim() ? `Press Enter to add "${query.trim()}"` : "Start typing…"}
              </CommandEmpty>
              <CommandGroup>
                {suggestions.map((s) => (
                  <CommandItem
                    key={s}
                    value={s}
                    onSelect={() => add(s)}
                    className="font-mono text-[12px] data-[selected=true]:bg-white data-[selected=true]:text-black cursor-pointer"
                  >
                    {s}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
